import {ChangeDetectorRef, ChangeDetectionStrategy, Component, Inject, ViewChild, NgZone, OnInit, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';



import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { ListManagerService } from 'shared/list-manager/list-manager.service';
import { AppRoutingService } from '../app-routing.service';
import { DigitalAssetsSearchService } from './search/digital-assets-search.service';
import { DigitalAssetsService } from 'shared/digital-assets/digital-assets.service';


import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { SearchPageSimpleHeaderComponent } from 'shared/search-page/search-page-simple-header/search-page-simple-header.component';
import { PublishModalComponent } from './components/publish-modal/publish-modal.component';
import { ContextualActionComponent } from './components/contextual-actions/contextual-action.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';
import { UploadAssetsComponent } from 'shared/digital-assets/upload-assets/upload-assets/upload-assets.component';

import { columns } from './search/search-results-columns';
import { ReturnRouteModel } from 'shared/return-route/return-route.model';
import { SearchModel } from 'shared/search/search.model';
import { showAnimation } from 'shared/search-page/animations';
import { actions } from './digital-assets-actions';
import {finalize, takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'sd-digital-assets',
  templateUrl: 'digital-assets.component.html',
  styleUrls: ['digital-assets.component.scss'],
  providers: [
    {provide: 'LIST_TYPE', useValue: 'asset'},
    ListManagerService,
  ],
  animations: [showAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitalAssetsComponent extends SearchPageComponent implements OnInit, OnDestroy {

  contentSelector: string = '#digital-assets-content';
  detailsPageRootRoute: string = 'digital-asset';
  duplicatedAssets: Array<any> = [];
  overwrittenCount: number = 0;
  rowsHover: boolean = true;
  actions: any = actions;
  searchInputPlaceholder: string = 'Search for a digital asset by name or other information...';
  ghostValue: string = 'dam';
  showSidedrawer: boolean = true; // use this field to show sidedrawer

  @ViewChild(PublishModalComponent, {static: false})
  publishModal: PublishModalComponent | void;

  @ViewChild('deleteModal', {static: false})
  deleteModal: SimpleConfirmModalComponent | void;

  @ViewChild('deleteAssignmentsModal')
  deleteAssignmentsModal: SimpleConfirmModalComponent;

  @ViewChild(ContextualActionComponent, {static: false})
  contextualAction: ContextualActionComponent | void;

  @ViewChild(UploadAssetsComponent, {static: false})
  uploadAssets: UploadAssetsComponent | void;

  @ViewChild(SearchPageSimpleHeaderComponent, {static: false})
  declare headerEl: SearchPageSimpleHeaderComponent | void;

  constructor(
    public searchService: DigitalAssetsSearchService,
    protected brandService: BrandService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected listsService: ListManagerService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected returnRouteService: ReturnRouteService,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
    private digitalAssetsService: DigitalAssetsService,
  ) {
    super(
      searchService,
      brandService,
      pageService,
      processService,
      scrollToService,
      appRoutingService,
      listsService,
      permissionsService,
      ngZone,
      ref,
      route,
      location,
      savedSearchesService,
      router,
      userService,
    );
    this.searchResultsColumns = columns;
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get headerColor(): string {
    return 'midgrey';
  }

  get selectedRowsSize(): number {
    // if selected all items return total records length
    if (this.allSelected) {
      return this.totalRecords;
    }
    // if there is no sort by status return all selected rows length
    if (!this.currentStatusModel) {
      return this.selectedRows.length;
    }
    // filter by status (assigned/unassigned)
    return _.filter( this.selectedRows, ['assigned',
      this.currentStatusModel.id
    ]).length;
  }

  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(localStorage.getItem(`digitalAssetsSearchModel.${this.brandService.getBrand()}.${this.user.id}`));
    } else {
      return null;
    }
  }

  get searchValue() {
    if (this.searchModel && this.searchModel.searchValue && this.searchModel.searchValue.length) {
      return this.searchModel.searchValue;
    }
    return null;
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    // apply permissions to actions
    const addAction = _.find(this.actions, { action: 'add-new-asset' });
    if (addAction) {
      // @ts-ignore
      addAction.visible = this.hasPermission(['admin', 'dam-edit']);
    }

    super.ngOnInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.duplicatedAssets = [];
  }

  storeSearchModel(searchModel: SearchModel) {
    if (_.has(this, 'user.id')) {
      localStorage.setItem(`digitalAssetsSearchModel.${this.brandService.getBrand()}.${this.user.id}`, JSON.stringify(searchModel));
    }
  }

  // inherited method of search-page component
  handlePageAction(action: string) {
    switch (action) {
      case 'add-asset':
        if (this.uploadAssets) {
          this.uploadAssets.showUploadModal();
        }
        break;
      default:
        break;
    }
  }

  sanitizeColumns(_columns: Array<any>): void {
      const availableColumns: Array<any> = this.getAllAvailableColumns();
      const cleanColumns: Array<any> = [];
      _.each(_columns, (column) => {
          const found = _.find(availableColumns, {'col': column.col} );
          if (found) {
            cleanColumns.push(found);
          }
      });
      if (cleanColumns.length) {
        this.searchResultsColumns = cleanColumns;
      }
  }

  getAllAvailableColumns(): Array<any> {
    return columns;
  }

  handleViewDetails() {
    const first = _.head(this.selectedRows);
    if (first) {
      this.navigateToDetailsPage(first._id);
    }
  }

  public hasPermission(allow: Array<string>|string, requireAll: boolean= false) {
    return this.permissionsService.hasPermission(allow, requireAll);
  }

  /*******************************************
   * COMPONENT EVENT HANDLERS
   *******************************************/

  onDetailsBtnClick(_id: string) {
    this.navigateToDetailsPage(_id);
  }

  onUploadingAssetsDone() {
    if (this.searchModel instanceof SearchModel) {
      this.runSearch(this.searchModel);
    }
  }

  onPublishModalShow() {
    if (this.publishModal) {
      this.publishModal.show();
    }
  }

  onPublishConfirm() {
    this.startLoading();
    if (this.contextualAction) {
      this.contextualAction.publishConfirm();
    }
  }

  onPublishConfirmed() {
    this.stopLoading();
  }

  onSearchCall() {
    if (this.searchModel instanceof SearchModel) {
      this.searchModel.currentPage = 1;
    }
    if (this.searchForm) {
      if (this.searchModel instanceof SearchModel) {
        this.searchModel.searchValue = this.searchForm.searchTermValue;
      }
    }
    if (this.searchModel instanceof SearchModel) {
      this._searchModel.next(this.searchModel);
    }
    this.searchService.toggleForm(false);
  }

  onCancelClick() {
    if (this.searchForm && this.searchModel) {
      this.searchForm.resetValue(this.searchModel.searchValue);
    }
    this.searchService.toggleForm(false);
  }

  onLinkClick($event: any) {
    if ($event.column.col === 'filename' || $event.column.col === 'photo') {
      this.navigateToDetailsPage($event.row._id);
    }
  }

  onDeleteSelectedhModalShow() {
    if (this.deleteModal) {
      this.deleteModal.show();
    }
  }

  onDeleteAssignmentsModalShow() {
    this.deleteAssignmentsModal.show();
  }

  onAssetsDelete(deleteRelations?: boolean) {
    this.startLoading();
    if (this.allSelected) {
      const filter: any = {
        searchValue: this.searchValue,
        status: String(_.get(this.searchModel, 'statusModel.id', '')),
        list: this.listId,
      };
      this.digitalAssetsService.deleteAssets(this.brandService.getBrand(), [], deleteRelations, filter)
          .pipe(finalize(() => this.stopLoading()), takeWhile(() => this.isPageActive))
        .subscribe(() => {
          this.fetchLists();
          this.deselectAll();
          if (this.searchModel instanceof SearchModel) {
            this._searchModel.next(this.searchModel);
          }
        });
    } else {
      this.digitalAssetsService.deleteAssets(
        this.brandService.getBrand(),
        _.map(this.selectedRows, '_id'),
        deleteRelations
      ).pipe(
          finalize(() => this.stopLoading()),
          takeWhile(() => this.isPageActive)
      )
        .subscribe(() => {
          this.fetchLists();
          this.deselectAll();
          if (this.searchModel instanceof SearchModel) {
            this._searchModel.next(this.searchModel);
          }
        });
    }
  }

  onAssetsAssignmentsDelete() {
    this.onAssetsDelete(true);
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private navigateToDetailsPage(assetId: string) {
    this.returnRouteService.push(new ReturnRouteModel(`/${this.route.snapshot.url.join('/')}`, { smlocal: 1}));
    this.router.navigate([ '/digital-asset', assetId || 1, 'details' ]);
  }
}
